import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listMyBugReports, submitBugReport } from "../api/bugReports";
import ModalCloseX from "../components/common/ModalCloseX";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";

const CATEGORY_OPTIONS = [
  "Booking",
  "Tickets",
  "Reports",
  "Time Clock",
  "Walk-In Queue",
  "Setup",
  "Other",
];

const SEVERITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

const STATUS_BADGES = {
  Open: "border-amber-200 bg-amber-50 text-amber-700",
  "In Progress": "border-blue-200 bg-blue-50 text-blue-700",
  Resolved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  Dismissed: "border-slate-200 bg-slate-100 text-slate-600",
};

const MAX_SCREENSHOT_MB = 5;
const MAX_SCREENSHOT_BYTES = MAX_SCREENSHOT_MB * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

const buildEmptyForm = () => ({
  title: "",
  category: "",
  severity: "",
  description: "",
  steps_to_reproduce: "",
  expected_result: "",
  actual_result: "",
});

export default function BugReport() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(buildEmptyForm());
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const [userAgent, setUserAgent] = useState("");
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [recentReports, setRecentReports] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const pageUrl = useMemo(() => {
    const path = location.pathname || "";
    const search = location.search || "";
    const hash = location.hash || "";
    return `${path}${search}${hash}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (typeof navigator !== "undefined") {
      setUserAgent(navigator.userAgent || "");
    }
  }, []);

  useEffect(() => {
    if (!screenshotFile) {
      setScreenshotPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(screenshotFile);
    setScreenshotPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshotFile]);

  const loadRecentReports = async () => {
    if (!token) return;
    setLoadingRecent(true);
    try {
      const data = await listMyBugReports(token, 5);
      setRecentReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setRecentReports([]);
    } finally {
      setLoadingRecent(false);
    }
  };

  useEffect(() => {
    loadRecentReports();
  }, [token]);

  const handleFieldChange = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSelectScreenshot = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_SCREENSHOT_TYPES.includes(file.type)) {
      setError("Screenshot must be a PNG, JPG, or WEBP image.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError(`Screenshot must be under ${MAX_SCREENSHOT_MB} MB.`);
      event.target.value = "";
      return;
    }
    setError("");
    setScreenshotFile(file);
  };

  const handleRemoveScreenshot = () => {
    setScreenshotFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    if (!form.title.trim()) return "Title is required.";
    if (!form.category) return "Category is required.";
    if (!form.severity) return "Severity is required.";
    if (!form.description.trim()) return "Description is required.";
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccessMessage("");
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      payload.append("category", form.category);
      payload.append("severity", form.severity);
      payload.append("description", form.description.trim());
      if (form.steps_to_reproduce.trim()) {
        payload.append("steps_to_reproduce", form.steps_to_reproduce.trim());
      }
      if (form.expected_result.trim()) {
        payload.append("expected_result", form.expected_result.trim());
      }
      if (form.actual_result.trim()) {
        payload.append("actual_result", form.actual_result.trim());
      }
      if (pageUrl) payload.append("page_url", pageUrl);
      if (userAgent) payload.append("user_agent", userAgent);
      if (screenshotFile) payload.append("screenshot", screenshotFile);

      const created = await submitBugReport(payload, token);
      setSuccessMessage(
        `Bug report submitted. Your report ID is #${created?.id}.`
      );
      setForm(buildEmptyForm());
      handleRemoveScreenshot();
      await loadRecentReports();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to submit bug report."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Bug Report
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="md:col-span-3 flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={handleFieldChange("title")}
                    className={FIELD_CLASS}
                    placeholder="Short summary of the issue"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Category</label>
                  <select
                    value={form.category}
                    onChange={handleFieldChange("category")}
                    className={FIELD_CLASS}
                    required
                  >
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Severity</label>
                  <select
                    value={form.severity}
                    onChange={handleFieldChange("severity")}
                    className={FIELD_CLASS}
                    required
                  >
                    <option value="">Select severity</option>
                    {SEVERITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3 flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Description</label>
                  <textarea
                    value={form.description}
                    onChange={handleFieldChange("description")}
                    className={`${FIELD_CLASS} h-auto min-h-[140px] py-3`}
                    placeholder="Describe the issue in detail."
                    required
                  />
                </div>
              </div>

              <details className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <summary className="cursor-pointer select-none px-4 py-3 bg-slate-50 text-sm md:text-base font-semibold text-slate-700">
                  More details
                </summary>
                <div className="p-4 space-y-4">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>Steps to Reproduce</label>
                    <textarea
                      value={form.steps_to_reproduce}
                      onChange={handleFieldChange("steps_to_reproduce")}
                      className={`${FIELD_CLASS} h-auto min-h-[120px] py-3`}
                      placeholder="Step-by-step instructions."
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Expected Result</label>
                      <textarea
                        value={form.expected_result}
                        onChange={handleFieldChange("expected_result")}
                        className={`${FIELD_CLASS} h-auto min-h-[120px] py-3`}
                        placeholder="What should have happened?"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Actual Result</label>
                      <textarea
                        value={form.actual_result}
                        onChange={handleFieldChange("actual_result")}
                        className={`${FIELD_CLASS} h-auto min-h-[120px] py-3`}
                        placeholder="What actually happened?"
                      />
                    </div>
                  </div>
                </div>
              </details>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-sm md:text-base font-semibold text-slate-900">
                      Screenshot (optional)
                    </div>
                    <div className="text-xs text-slate-500">
                      PNG, JPG, or WEBP. Max {MAX_SCREENSHOT_MB} MB.
                    </div>
                  </div>
                  <button
                    type="button"
                    className={BTN_OUTLINE}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {screenshotFile ? "Replace" : "Upload"}
                  </button>
                </div>
                <input
                  type="file"
                  accept={ALLOWED_SCREENSHOT_TYPES.join(",")}
                  ref={fileInputRef}
                  onChange={handleSelectScreenshot}
                  className="hidden"
                />
                {screenshotFile && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-3">
                    {screenshotPreview && (
                      <img
                        src={screenshotPreview}
                        alt="Screenshot preview"
                        className="max-h-60 w-auto rounded-xl border border-slate-200"
                      />
                    )}
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{screenshotFile.name}</span>
                      <button
                        type="button"
                        onClick={handleRemoveScreenshot}
                        className="text-rose-600 font-semibold hover:text-rose-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 space-y-2">
                <div>
                  <span className="font-semibold text-slate-700">Current page:</span>{" "}
                  {pageUrl || "--"}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Browser:</span>{" "}
                  {userAgent || "--"}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" className={BTN_PRIMARY} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Bug Report"}
                </button>
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() => {
                    setForm(buildEmptyForm());
                    handleRemoveScreenshot();
                    setError("");
                    setSuccessMessage("");
                  }}
                >
                  Reset
                </button>
              </div>
            </form>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    My recent bug reports
                  </div>
                  <div className="text-sm text-slate-500">
                    Showing your latest submissions.
                  </div>
                </div>
                {loadingRecent && (
                  <span className="text-xs text-slate-500">Loading...</span>
                )}
              </div>
              {recentReports.length === 0 && !loadingRecent ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-sm text-slate-500">
                  No bug reports submitted yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {recentReports.map((report) => {
                    const badgeClass =
                      STATUS_BADGES[report.status] ||
                      "border-slate-200 bg-slate-100 text-slate-600";
                    return (
                      <div
                        key={report.id}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 flex flex-wrap items-center justify-between gap-2"
                      >
                        <div>
                          <div className="text-sm font-semibold text-slate-900">
                            {report.title}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatDateOnlyYYYYMMDD(report.created_local_date)} -{" "}
                            {report.category}
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${badgeClass}`}
                        >
                          {report.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
